import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import '../../css/components/Notification/NotificationBell.css';

import { useUser } from '../../hooks/useUser';
import { roleConfig } from '../../constants/roleConfig';

import {
    getNotifications,
    getUnreadNotificationCount,
    markNotificationAsRead,
    markAllNotificationsAsRead
} from '../../service/api/Notification/apiNotification';

const NotificationBell = () => {
    const navigate = useNavigate();
    const { id: userId, role } = useUser();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);

    // 取得未讀數量
    const fetchUnreadCount = async () => {
        if (!userId || role < roleConfig['財務']) return; // 只有 role >= 財務 才能看到通知

        try {
            const response = await getUnreadNotificationCount({ receiverId: userId, receiverRole: role });
            if (response?.status === 200 && response.data?.status === 1) {
                setUnreadCount(response.data.count);
            }
        } catch (error) {
            console.error('取得未讀通知數量失敗:', error);
        }
    };

    // 取得通知列表
    const fetchNotifications = async () => {
        if (!userId || role < roleConfig['財務']) return; // 只有 role >= 財務 才能看到通知

        setLoading(true);
        try {
            const response = await getNotifications({ receiverId: userId, receiverRole: role });
            if (response?.status === 200 && response.data?.status === 1) {
                setNotifications(response.data.result || []);
            }
        } catch (error) {
            console.error('取得通知失敗:', error);
        } finally {
            setLoading(false);
        }
    };

    // 標記單則為已讀
    const handleMarkAsRead = async (notificationId) => {
        try {
            const response = await markNotificationAsRead(notificationId, { userId });
            if (response?.status === 200 && response.data?.status === 1) {
                setNotifications(prev =>
                    prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
                );
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (error) {
            console.error('標記已讀失敗:', error);
        }
    };

    // 標記全部為已讀
    const handleMarkAllAsRead = async () => {
        try {
            const response = await markAllNotificationsAsRead({ receiverId: userId, receiverRole: role });
            if (response?.status === 200 && response.data?.status === 1) {
                setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
                setUnreadCount(0);
            }
        } catch (error) {
            console.error('標記全部已讀失敗:', error);
        }
    };

    // 點擊外部關閉
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 定期刷新未讀數量
    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 30000); // 每30秒刷新
        return () => clearInterval(interval);
    }, [userId, role]);

    // 打開下拉選單時載入通知
    useEffect(() => {
        if (isOpen) {
            fetchNotifications();
        }
    }, [isOpen]);

    // 如果不是 role >= 財務，不顯示
    if (!userId || role < roleConfig['財務']) {
        return null;
    }

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'truck_loan_request':
                return '🚚';
            case 'truck_return_request':
                return '🔄';
            case 'truck_loan_approved':
                return '✅';
            case 'truck_loan_cancel_approved':
                return '✅';
            case 'truck_loan_cancel_rejected':
                return '❌';
            case 'truck_loan_rejected':
                return '❌';
            default:
                return '📢';
        }
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = now - date;
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (minutes < 1) return '剛剛';
        if (minutes < 60) return `${minutes} 分鐘前`;
        if (hours < 24) return `${hours} 小時前`;
        if (days < 7) return `${days} 天前`;
        return date.toLocaleDateString('zh-TW');
    };

    return (
        <div className="notification-bell-container" ref={dropdownRef}>
            <button
                className="notification-bell-btn"
                onClick={() => setIsOpen(!isOpen)}
            >
                <i className="fas fa-bell"></i>
                {unreadCount > 0 && (
                    <span className="notification-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="notification-dropdown">
                    <div className="notification-header">
                        <h4>通知</h4>
                        {unreadCount > 0 && (
                            <button
                                className="mark-all-read-btn"
                                onClick={handleMarkAllAsRead}
                            >
                                全部標為已讀
                            </button>
                        )}
                    </div>

                    <div className="notification-list">
                        {loading ? (
                            <div className="notification-loading">載入中...</div>
                        ) : notifications.length === 0 ? (
                            <div className="notification-empty">沒有通知</div>
                        ) : (
                            notifications.map(notification => (
                                <div
                                    key={notification.id}
                                    className={`notification-item ${!notification.isRead ? 'unread' : ''} clickable`}
                                    onClick={() => {
                                        // 標記已讀
                                        if (!notification.isRead) {
                                            handleMarkAsRead(notification.id);
                                        }
                                        // 如果是借車申請相關，跳轉到審核頁面
                                        if (notification.type === 'truck_loan_request') {
                                            setIsOpen(false);
                                        }
                                        if (role >= roleConfig['協理']) navigate('/SKAManager/TruckLoanApproval');
                                    }}
                                >
                                    <span className="notification-icon">
                                        {getNotificationIcon(notification.type)}
                                    </span>
                                    <div className="notification-content">
                                        <div className="notification-title">{notification.title}</div>
                                        <div className="notification-text">{notification.content}</div>
                                        <div className="notification-time">
                                            {formatTime(notification.createdAt)}
                                        </div>
                                    </div>
                                    {!notification.isRead && (
                                        <span className="notification-unread-dot"></span>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export { NotificationBell };
